/**
 * Conversation Compacting Hook
 * Auto-compacts long conversations to save tokens
 */

import { useCallback, useRef } from 'react';
import { ConversationCompactor } from '../lib/conversation-compactor';
import { getUnifiedClient } from '../lib/unified-model-client';
import { useAppStore } from '../store/app-store';

export function useConversationCompactor() {
  const compactor = useRef(null);
  const { activeConversationId, updateConversation, getActiveConversation } = useAppStore();

  // Initialize compactor
  if (!compactor.current) {
    try {
      const modelClient = getUnifiedClient();
      compactor.current = new ConversationCompactor(modelClient);
    } catch (error) {
      console.warn('[Compactor] Not initialized:', error.message);
    }
  }

  /**
   * Check if conversation needs compacting
   */
  const needsCompacting = useCallback((messages) => {
    if (!compactor.current || !messages) return false;
    return compactor.current.needsCompacting(messages);
  }, []);

  /**
   * Get token stats for conversation
   */
  const getStats = useCallback((messages) => {
    if (!compactor.current || !messages) return null;
    return compactor.current.getStats(messages);
  }, []);

  /**
   * Compact conversation if needed
   */
  const compactIfNeeded = useCallback(async (onProgress) => {
    if (!compactor.current) {
      console.warn('[Compactor] Not available');
      return { compacted: false };
    }

    const conversation = getActiveConversation();
    if (!conversation || !conversation.messages) {
      return { compacted: false };
    }

    if (onProgress) {
      onProgress('Checking if compacting needed...');
    }

    const result = await compactor.current.autoCompact(conversation.messages);

    if (result.compacted) {
      if (onProgress) {
        onProgress(`Compacted! Saved ${result.stats.percentSaved}% tokens`);
      }

      // Update conversation with compacted messages
      updateConversation(conversation.id, {
        messages: result.messages
      });

      console.log('[Compactor] ✅ Compacted conversation:', result.stats);
    }

    return result;
  }, [activeConversationId, getActiveConversation, updateConversation]);

  /**
   * Get current conversation stats
   */
  const getCurrentStats = useCallback(() => {
    const conversation = getActiveConversation();
    if (!conversation || !conversation.messages) return null;
    return getStats(conversation.messages);
  }, [activeConversationId, getActiveConversation, getStats]);

  return {
    needsCompacting,
    getStats,
    compactIfNeeded,
    getCurrentStats,
    isAvailable: !!compactor.current
  };
}

export default useConversationCompactor;
